import { type FC, useMemo } from "react";
import { useSelector } from "react-redux";

import { type IMaxHitRange, getMaxHitRange } from "../../../../app-utils/blast.js";
import { type BlastResult } from "../../../../graphql.js";
import { getTableDataSlice, isColumnExpanded } from "../../../../redux/modules/table-data.js";
import { type IAppState } from "../../../../redux/reducer.js";
import HitGraphics from "../../../blast-result/HitGraphics.js";
import { type IAgGridCellProps } from "../cell-base-interfaces.js";
import { traverseAndCollect } from "../column-props-factory.js";
import { correspondsToFieldPath } from "./core/LinkListCell.js";
import ListCellContent, { type ListCellItem } from "./core/ListCellContent.js";
import { TableCell } from "./core/TableCell.js";

export const createHitLink = (maxHitRange: IMaxHitRange, queryProteinLength: number, blastResult: BlastResult) => {
  return <HitGraphics maxHitRange={maxHitRange} queryProteinLength={queryProteinLength} blastResult={blastResult} />;
};

function generateItems(
  field: string,
  listObjects: any[],
  proteinLength: number,
  cellExpanded: boolean,
): ListCellItem[] {
  if (correspondsToFieldPath(field, "bestBlastResults.alignment")) {
    if (cellExpanded) {
      const maxHitRange: IMaxHitRange = getMaxHitRange(proteinLength, listObjects);

      return listObjects.map((blastResult: BlastResult) => ({
        value: createHitLink(maxHitRange, proteinLength, blastResult),
        tooltip: "Best hit alignment",
      }));
    } else {
      return [
        {
          value: listObjects.length > 0 ? <span>[collapsed]</span> : null,
          tooltip: "Expand this column to see the alignments.",
        },
      ];
    }
  }
}

const HitListCell: FC<IAgGridCellProps> = (props) => {
  if (!props.value || !props.data) {
    return null;
  }

  const { data, colDef } = props;
  const { field, groupField } = colDef;

  const listObjects: any[] = traverseAndCollect(data, groupField, true);
  //  .filter((o) => o !== null && o !== undefined); // instead of !o os that the 0 value still is valid

  // This unwinds any list structure along the path of the groupField
  // The remaining sub-path of the field must match one of the handled path in generateItems
  // and the resulting data structure in the listObject must match that sub-path!
  // Remember the handling of
  //  "proteinLocations.region.contig.accession" with "proteinLocations.region" as groupField  creates a list of "contig"
  //  while "mutVariants.region.contig.accession" with "mutVariants" as groupField creates a list of "region"
  //

  // we need to retrieve the length of the current protein, which must be done differently for plain protein and for protein location etc. tables

  let proteinLength = 0;
  if (colDef.field.startsWith("protein.")) {
    proteinLength = data.protein.length;
  } else if (colDef.field.startsWith("proteinLocation.")) {
    proteinLength = data.proteinLocation.protein.length;
  } else {
    proteinLength = data.length;
  }

  const expansionPath = useSelector<IAppState, string[]>((state) =>
    props.context?.tableSpec ? getTableDataSlice(state.tableData, props.context?.tableSpec)?.expansionPath : null,
  );
  const cellExpanded = useMemo<boolean>(() => {
    return isColumnExpanded(props.colDef, expansionPath);
  }, [expansionPath]);

  const items = generateItems(field, listObjects, proteinLength, cellExpanded);

  return (
    <TableCell>
      <ListCellContent items={items} colDef={props.colDef} context={props.context} />
    </TableCell>
  );
};

export default HitListCell;
